import {
  forwardRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { cn } from 'src/lib/utils';
import { extractSystemProps } from 'src/components/md/system-props';

/**
 * Tailwind-based replacement for the former `styled(Button)` MDButton root.
 * Maps the MD color/variant/size tokens to literal Tailwind class strings (kept literal
 * so Tailwind's source scanner preserves them) and delegates system props + `sx` to
 * {@link extractSystemProps}.
 */

interface OwnerState {
  color: string;
  variant: 'text' | 'contained' | 'outlined' | 'gradient';
  size: 'small' | 'medium' | 'large';
  circular: boolean;
  iconOnly: boolean;
  darkMode: boolean;
}

const BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-bold uppercase tracking-wide leading-normal transition-all duration-150 select-none whitespace-nowrap disabled:opacity-65 disabled:pointer-events-none focus:outline-none';

const SIZE: Record<string, string> = {
  small: 'min-h-8 px-4 py-1.5 text-xs',
  medium: 'min-h-10 px-6 py-2.5 text-xs',
  large: 'min-h-[47px] px-7 py-3 text-sm',
};

const ICON_ONLY_SIZE: Record<string, string> = {
  small: 'h-[25px] w-[25px] min-h-0 p-0',
  medium: 'h-[38px] w-[38px] min-h-0 p-0',
  large: 'h-[52px] w-[52px] min-h-0 p-0',
};

const CONTAINED: Record<string, string> = {
  white: 'bg-white text-stone-800 shadow-sm hover:bg-stone-100',
  primary: 'bg-stone-900 text-white hover:bg-stone-800',
  secondary: 'bg-stone-600 text-white hover:bg-stone-700',
  info: 'bg-sky-500 text-white hover:bg-sky-600',
  success: 'bg-lime-500 text-white hover:bg-lime-600',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
  error: 'bg-red-500 text-white hover:bg-red-600',
  light: 'bg-stone-200 text-stone-800 hover:bg-stone-300',
  dark: 'bg-stone-900 text-white hover:bg-stone-800',
};

const GRADIENT: Record<string, string> = {
  white: 'bg-white text-stone-800 shadow-sm hover:bg-stone-100',
  primary: 'bg-gradient-to-br from-stone-800 to-stone-950 text-white',
  secondary: 'bg-gradient-to-br from-stone-600 to-stone-800 text-white',
  info: 'bg-gradient-to-br from-sky-500 to-sky-600 text-white',
  success: 'bg-gradient-to-br from-lime-500 to-lime-600 text-white',
  warning: 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-white',
  error: 'bg-gradient-to-br from-red-500 to-red-600 text-white',
  light: 'bg-gradient-to-br from-stone-200 to-stone-300 text-stone-800',
  dark: 'bg-gradient-to-br from-stone-800 to-stone-950 text-white',
};

const OUTLINED: Record<string, string> = {
  white: 'border border-white/75 text-white bg-transparent hover:bg-white/10',
  primary:
    'border border-stone-900 text-stone-900 bg-transparent hover:bg-stone-900/10',
  secondary:
    'border border-stone-600 text-stone-600 bg-transparent hover:bg-stone-600/10',
  info: 'border border-sky-500 text-sky-500 bg-transparent hover:bg-sky-500/10',
  success:
    'border border-lime-500 text-lime-600 bg-transparent hover:bg-lime-500/10',
  warning:
    'border border-yellow-500 text-yellow-600 bg-transparent hover:bg-yellow-500/10',
  error:
    'border border-red-500 text-red-500 bg-transparent hover:bg-red-500/10',
  light:
    'border border-stone-200 text-stone-500 bg-transparent hover:bg-stone-200/40',
  dark: 'border border-stone-900 text-stone-900 bg-transparent hover:bg-stone-900/10',
};

const TEXT: Record<string, string> = {
  white: 'text-white bg-transparent hover:bg-white/10',
  primary: 'text-stone-900 bg-transparent hover:bg-stone-900/5',
  secondary: 'text-stone-600 bg-transparent hover:bg-stone-600/5',
  info: 'text-sky-500 bg-transparent hover:bg-sky-500/5',
  success: 'text-lime-600 bg-transparent hover:bg-lime-500/5',
  warning: 'text-yellow-600 bg-transparent hover:bg-yellow-500/5',
  error: 'text-red-500 bg-transparent hover:bg-red-500/5',
  light: 'text-stone-500 bg-transparent hover:bg-stone-200/40',
  dark: 'text-stone-900 bg-transparent hover:bg-stone-900/5',
};

function variantClasses(variant: string, color: string): string {
  const c = color in CONTAINED ? color : 'white';
  switch (variant) {
    case 'outlined':
      return OUTLINED[c];
    case 'text':
      return TEXT[c];
    case 'gradient':
      return GRADIENT[c];
    default:
      return CONTAINED[c];
  }
}

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  ownerState: OwnerState;
  children?: ReactNode;
  [key: string]: unknown;
}

const MDButtonRoot = forwardRef<HTMLButtonElement, Props>(
  function MDButtonRoot(props, ref) {
    const { ownerState, className, children, type, ...rest } =
      props as Props & { ownerState: OwnerState };
    const { color, variant, size, circular, iconOnly } = ownerState;

    const classes = cn(
      BASE,
      iconOnly ? ICON_ONLY_SIZE[size] : SIZE[size],
      variantClasses(variant, color),
      circular && 'rounded-full',
      className as string | undefined,
    );

    const { style, rest: domProps } = extractSystemProps(rest);

    return (
      <button
        ref={ref}
        type={(type as 'button' | 'submit' | 'reset') ?? 'button'}
        className={classes}
        style={style as CSSProperties}
        {...domProps}
      >
        {children}
      </button>
    );
  },
);

export default MDButtonRoot;
